//import "./safetyforms.css";
import moment from 'moment';
import * as Auth from 'aws-amplify/auth'
import { useContext, useEffect, useState } from "react";
import "react-dropdown/style.css";
import { useForm } from "react-hook-form";
import { FaExclamationTriangle, FaFilePdf, FaRecycle, FaSave, FaUser } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { reset } from '../common/slices/globalActions';
import { fetchSessions, modifyLocation, selectActiveLocation, selectSessions } from "../common/slices/location";
import { fetchDailyTimeReports, fetchPayrollReport, fetchTimeEntriesByStatus, fetchUserListReport, modifyTimeEntry, selectTimeEntries } from "../common/slices/timesheet";
import { ErrorContext } from '../ErrorContext';
import capitalizeFirstLetter from "../utils/capitalizeFirstLetter";
import { BlackButton, LightButton } from "./Buttons";
import Divider from "./Divider";
import FormDatePicker from "./FormDatePicker";
import FormMultiEmailInput from "./FormMultiEmailInput";
import FormPhoneNumber from "./FormPhoneNumber";
import FormSelectCrewMember from './FormSelectCrewMember';
import FormSelectCustomer from './FormSelectCustomer';
import FormSelectScopeOfWork from './FormSelectScopeOfWork';
import FormSelectSyncTasks from "./FormSelectSyncTasks";
import FormSelectTimezone, { getInitial } from './FormSelectTimezone';
import FormTextInput from "./FormTextInput";
import Layout, { Content } from "./Layout";
import ModalConfirm from "./ModalConfirm";
import ModalEditCrewMember from './ModalEditCrewMember';
import ModalEditCustomer from './ModalEditCustomer';
import ModalEditScopeOfWork from './ModalEditScopeOfWork';
import Row from "./Row";
import { MenuItem, Table, TableData, TableDataLast, TableHeader, TableMenu, TableRow } from "./Table";
import { EmptyTab, Tab, TabContainer, TabContent } from "./Tabs";
import { H1, P, P2, Pmb } from "./Typography";


const TimeEntryErrors = ({ locationId }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const timeEntries = useSelector(selectTimeEntries);
    const [showConfirm, setShowConfirm] = useState(false);

    const errorEntries = timeEntries.filter(te => te.status === "ERROR");

    useEffect(() => {
        (async () => {
            try {
                await dispatch(fetchTimeEntriesByStatus(locationId, 'ERROR'))
            } catch (ex) {
                errorContext.setError({ errorMessage: "Failed to fetch time entries by status", forSupport: ex.message });
            }
        })()
    }, [locationId, dispatch])

    const onRetryClick = async () => {
        try {
            await dispatch(modifyTimeEntry({ ...showConfirm, status: 'PENDING', syncStatusMessage: '' }));
            setShowConfirm(false);
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to update time entry", forSupport: ex.message });
        }
    }

    return (
        <Table>
            <thead>
                <tr>
                    <TableHeader style={{ width: '16rem' }}>ID</TableHeader>
                    <TableHeader>Date</TableHeader>
                    <TableHeader>Crew Member</TableHeader>
                    <TableHeader>Hours</TableHeader>
                    <TableHeader>Status Message</TableHeader>
                </tr>
            </thead>

            <tbody>
                {
                    errorEntries.map(te => (
                        <TableRow key={te.id}>
                            <TableData>{te.id}</TableData>
                            <TableData>{moment(te.date).format('MMM D, YYYY')}</TableData>
                            <TableData>{te.crewMember?.name}</TableData>
                            <TableData>{moment.duration({ hours: te.hours }).format('h:mm')}</TableData>
                            <TableDataLast>
                                {te.syncStatusMessage}
                                <TableMenu>
                                    <MenuItem onClick={(e) => setShowConfirm(te)}>
                                        Retry
                                        <FaRecycle style={{ marginLeft: "1rem" }} />
                                    </MenuItem>
                                </TableMenu>
                            </TableDataLast>
                        </TableRow>
                    ))
                }
                {
                    errorEntries.length === 0 &&
                    <TableRow><TableData colSpan={5}>No time entry sync errors</TableData></TableRow>
                }
            </tbody>

            {showConfirm &&
                <ModalConfirm
                    title="Retry Time Entry?"
                    description="Are you sure you want to set this time entry back to PENDING to be staged for the next web connector sync?"
                    confirmButtonTitle="Retry"
                    onConfirm={onRetryClick}
                    onClose={() => setShowConfirm(false)}
                />
            }

        </Table>
    )
}

const QBSyncStatus = ({ locationId }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const sessions = useSelector(selectSessions)
    useEffect(() => {
        (async () => {
            try {
                await dispatch(fetchSessions(locationId))
            } catch (ex) {
                errorContext.setError({ errorMessage: "Failed to load QBs sessions", forSupport: ex.message });
            }
        })()
    }, [locationId, dispatch])

    return (
        <Table>
            <thead>
                <tr>
                    <TableHeader style={{ width: '16rem' }}>Session Date / Time</TableHeader>
                    <TableHeader>Tasks</TableHeader>
                </tr>
            </thead>

            <tbody>
                {
                    sessions.map(session => (
                        <TableRow key={session.id}>
                            <TableData>{moment(session.createdAt).format('MMM DD, YYYY [at] h:mm a')}</TableData>
                            <TableData>{session.tasks?.items.map(task => `${task.task} - ${task.status}`).join(', ')}</TableData>
                        </TableRow>
                    ))
                }
                {
                    sessions.length === 0 &&
                    <TableRow><TableData colSpan={4}>No quick books syncs</TableData></TableRow>
                }
            </tbody>
        </Table>
    )
}

const QBSyncErrors = ({ tabId = "QBSYNC_ERRORS", location }) => {
    return (
        <TabContent tabId={tabId} style={{ paddingTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Pmb>Time Entry Issues</Pmb>
            <P>The table below shows the most recent sync errors.</P>
            <TimeEntryErrors locationId={location?.id} />
        </TabContent>
    )
}

const QBSync = ({ tabId = "QBSYNC", location, onSubmit }) => {
    const [editCrewMember, setEditCrewMember] = useState(undefined);
    const [editCustomer, setEditCustomer] = useState(undefined);
    const [editScopeOfWork, setEditScopeOfWork] = useState(undefined);

    const values = {
        ...location,
        syncTasks: location?.syncTasks?.map(st => ({ value: st, label: capitalizeFirstLetter(st) }))
    }
    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isValid, isSubmitting }
    } = useForm({
        defaultValues: {
            syncTasks: []
        },
        values
    })

    const formProps = { control, errors, locationId: location?.id };

    const prepareSubmit = (data) => {
        onSubmit({
            ...data,
            syncTasks: data.syncTasks?.map(st => st.value)
        })
    }

    const [customerId, scopeId, crewMemberId] = watch(["customerId", "scopeId", "crewMemberId"]);

    useEffect(() => {
        setEditCrewMember(crewMemberId?.value);
    }, [crewMemberId?.value])

    useEffect(() => {
        setEditCustomer(customerId?.value);
    }, [customerId?.value])

    useEffect(() => {
        setEditScopeOfWork(scopeId?.value);
    }, [scopeId?.value])


    return (
        <TabContent tabId={tabId} style={{ paddingTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Pmb>Pay Period {moment(location?.payPeriodStart).format('MMMM DD, YYYY')} - {moment(location?.payPeriodStart).add(13, "d").format('MMMM DD, YYYY')}</Pmb>
            <P>Use the form below to control the settings for the Quick Books Synchronization.</P>
            <Row style={{ alignItems: 'flex-start', gap: '2rem' }}>
                <div style={{ flex: 1, display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                    <Pmb>QuickBooks Data</Pmb>
                    <FormSelectCrewMember showAll {...formProps} name="crewMemberId" label="Crew Members" placeholder="Select crew member" />
                    <FormSelectCustomer {...formProps} name="customerId" label="Customers" placeholder="Select customer" />
                    <FormSelectScopeOfWork {...formProps} name="scopeId" label="Scope of Work" placeholder="Select scope of work" />
                </div>
                <Divider.Vertical />
                <div style={{ flex: 1, display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                    <FormSelectSyncTasks name="syncTasks" label="Sync Tasks" placeholder="Select sync tasks" {...formProps} rules={{ required: true }} />
                    <Row>
                        <BlackButton onClick={async (e) => {
                            setValue('syncTasks', [
                                { value: 'CUSTOMER', label: 'Customer' },
                                { value: 'TIME', label: 'Time' },
                                { value: 'EMPLOYEE', label: 'Employee' },
                                { value: 'PAYROLL_ITEM', label: 'Payroll Items' },
                            ])
                            await handleSubmit(prepareSubmit)(e)
                        }
                        }>Sync All</BlackButton>
                        <BlackButton onClick={handleSubmit(prepareSubmit)}>Save <FaSave /></BlackButton>
                    </Row>
                </div>
            </Row>
            <Divider.Horizontal />

            <Pmb>Quick Books Web Connector Status</Pmb>
            <P>The table below shows the most recent sync tasks.</P>
            <QBSyncStatus locationId={location?.id} />

            {editCrewMember && <ModalEditCrewMember crewMemberId={editCrewMember} onClose={() => {
                setEditCrewMember(null)
                setValue('crewMemberId', null);
            }} />}

            {editCustomer && <ModalEditCustomer customerId={editCustomer} onClose={() => {
                setEditCustomer(null)
                setValue('customerId', null);
            }} />}

            {editScopeOfWork && <ModalEditScopeOfWork scopeId={editScopeOfWork} onClose={() => {
                setEditScopeOfWork(null)
                setValue('scopeId', null);
            }} />}
        </TabContent>
    )
}

const GeneralSettings = ({ tabId = "GENERAL", location, onSubmit }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();

    const {
        control,
        handleSubmit,
        formState: { isSubmitting, errors }
    } = useForm({
        values: location
    })

    const formProps = { control, errors };

    const generateUserListReport = async () => {
        try {
            await dispatch(fetchUserListReport())
            toast.success("User list report generation in progress.  You will be notified when it is completed.")
        } catch (ex) {
            errorContext({ setError: "Failed to generate user list report", forSupport: ex.message })
        }
    }

    return (
        <TabContent tabId={tabId} style={{ paddingTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <P>Edit settings specific to the company you are viewing.</P>

            <Row>
                <FormTextInput name="name" {...formProps} label="Company Name" placeholder="Enter company name for document exports" />
                <FormPhoneNumber name="phoneNumber" {...formProps} label="Company Phone" placeholder="Enter company phone number" />
            </Row>

            <Divider.Horizontal />

            <FormTextInput name="address" {...formProps} label="Address" placeholder="Enter company address" />
            <FormTextInput name="address1" {...formProps} label="Address Second Line" placeholder="Enter company address" />
            <Row>
                <FormTextInput name="city" {...formProps} label="City" placeholder="Enter city" />
                <FormTextInput name="province" {...formProps} label="Province" placeholder="Enter province" />
                <FormTextInput name="postalCode" {...formProps} label="Postal Code" placeholder="Enter postal code" />
            </Row>

            <Divider.Horizontal />

            <Row>
                <BlackButton onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>Save <FaSave /></BlackButton>
                <BlackButton onClick={handleSubmit(generateUserListReport)} disabled={isSubmitting}>Export User List <FaUser /></BlackButton>
            </Row>
        </TabContent>
    )
}

const PayrollSettings = ({ tabId = "PAYROLL", location, onSubmit }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const {
        control,
        handleSubmit,
        formState: { errors, isValid, isSubmitting }
    } = useForm({
        values: {
            ...location,
            utcOffset: getInitial(location?.utcOffset)
        }
    })

    const unmarshall = (values) => ({ ...values, utcOffset: values?.utcOffset?.value })

    const formProps = { control, errors };

    const onGeneratePayrollReport = async (formData) => {
        try {
            await dispatch(modifyLocation(unmarshall(formData)));
            await dispatch(fetchPayrollReport({ locationId: formData.id, startDate: formData.payPeriodStart }));
            toast.success("Payroll report generation in progress.  You will be notified when it is completed.")
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to generate payroll report", forSupport: ex.message })
        }
    }

    const sendDailyTimeReport = async (formData) => {
        try {
            await dispatch(modifyLocation(unmarshall(formData)));
            await dispatch(fetchDailyTimeReports());
            toast.success("Daily time report generation in progress.  You will be notified when it is completed.")
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to send daily time report", forSupport: ex.message })
        }
    }

    return (
        <TabContent tabId={tabId} style={{ paddingTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <P>Use the form below to control the settings required for payroll calculation of straight time and overtime.</P>

            <P2>Calculate daily overtime after</P2>

            <Row style={{ alignItems: 'flex-start', gap: '2rem' }}>
                <div style={{ flex: 1, display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                    <FormTextInput name="maxDailyHours" {...formProps} label="Hours Per Day" placeholder="Enter max number of hours per day before OT is calculated" rules={{ required: true }} />
                    <FormTextInput name="maxWeeklyHours" {...formProps} label="Hours Per Week" placeholder="Enter max number of hours per week before OT is calculated" rules={{ required: true }} />
                    <FormSelectTimezone name="utcOffset" {...formProps} label="UTC Offset" placeholder="Set the UTC offset for the location" rules={{ required: true }} />
                </div>
                <Divider.Vertical />
                <div style={{ flex: 1, display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                    <FormDatePicker name="payPeriodStart" {...formProps} label="Pay Period Start Day" placeholder="Select the day when the pay period starts" rules={{ required: true }} containerStyle={{ flex: 1 }} />
                    <FormMultiEmailInput {...formProps} name="dailyTimeReportReceivers" label="Daily Time Report Receivers" placeholder="Enter email adresses of users who should receive daily time reports" style={{ flex: 1 }} />
                </div>
            </Row>
            <Divider.Horizontal />
            <Row>
                <BlackButton onClick={handleSubmit((data) => onSubmit(unmarshall(data)))} disabled={!isValid || isSubmitting}>Save <FaSave /></BlackButton>
                <BlackButton onClick={handleSubmit(onGeneratePayrollReport)} style={{ alignSelf: 'flex-start' }} disabled={isSubmitting}>Generate Payroll Report <FaFilePdf /></BlackButton>
                <BlackButton onClick={handleSubmit(sendDailyTimeReport)} style={{ alignSelf: 'flex-start' }} disabled={isSubmitting}>Email Daily Time Report <FaFilePdf /></BlackButton>
            </Row>
        </TabContent>
    )
}

const AccountSettings = ({ tabId = "ACCOUNT" }) => {
    const dispatch = useDispatch();
    const nav = useNavigate();
    const [showConfirm, setShowConfirm] = useState(false);

    const onLogout = async () => {
        await Auth.signOut();
        dispatch(reset());
        nav('/');
    }

    return (
        <TabContent tabId={tabId} style={{ paddingTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <P>Sign out of your account by clicking the button below.</P>
            <Row>
                <LightButton onClick={() => setShowConfirm(true)}>Logout <FaExclamationTriangle /></LightButton>
            </Row>
            {
                showConfirm && <ModalConfirm
                    onConfirm={onLogout}
                    title="Logout?"
                    description="Are you sure you want to logout"
                    confirmButtonTitle="Yes"
                    onClose={() => setShowConfirm(false)}
                />
            }
        </TabContent>
    )
}

const AppSettings = ({ tabId = "APP_SETTINGS", location, onSubmit }) => {
    const {
        control,
        handleSubmit,
        formState: { errors, isValid, isSubmitting }
    } = useForm({
        values: location
    })

    const formProps = { control, errors };

    return (
        <TabContent tabId={tabId} style={{ paddingTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <P>Use the form below to control the settings for the mobile application.</P>

            <Row style={{ alignItems: 'flex-start', gap: '2rem' }}>
                <div style={{ flex: 1, display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                    <FormTextInput name="termsOfUseURL" {...formProps} label="Terms of Use URL" placeholder="Enter the terms of use url for the mobile app" rules={{ required: true }} />
                    <FormTextInput name="privacyURL" {...formProps} label="Privacy Policy URL" placeholder="Enter the privacy policy url for the mobile app" rules={{ required: true }} />
                    <FormTextInput name="supportURL" {...formProps} label="Support URL" placeholder="Enter the support url for the mobile app" rules={{ required: true }} />
                </div>
                <Divider.Vertical />
                <div style={{ flex: 1, display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                    <FormTextInput name="mediaTargetFilesize" {...formProps} label="Media Target Filesize (MB)" placeholder="What is the target filesize of assets in MB (0.1 - 15)?" rules={{ required: true, min: 0.1, max: 15 }} />
                    <FormTextInput name="mediaCompression" {...formProps} label="Media Compression (%)" placeholder="To enable image compression, enter a value (0-1)" rules={{ required: true, min: 0, max: 1 }} />
                </div>
            </Row>
            <Divider.Horizontal />
            <Row>
                <BlackButton onClick={handleSubmit(onSubmit)} disabled={!isValid || isSubmitting}>Save <FaSave /></BlackButton>
            </Row>
        </TabContent>
    )
}

const ScreenSettings = () => {
    const errorContext = useContext(ErrorContext)
    const dispatch = useDispatch();
    const location = useSelector(selectActiveLocation);

    const onSubmit = async (newLocation) => {
        try {
            await dispatch(modifyLocation(newLocation))
            toast.success("Location Updated");
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to update location", forSupport: ex.message })
        }
    }

    return (
        <Layout>
            <H1 style={{ marginTop: '2rem' }}>Settings</H1>

            <Content>
                <TabContainer initialTab="GENERAL">
                    <Row>
                        <Tab tabId="GENERAL">General</Tab>
                        <Tab tabId="PAYROLL">Payroll & Overtime</Tab>
                        <Tab tabId="QBSYNC">QuickBooks</Tab>
                        <Tab tabId="QBSYNC_ERRORS">QuickBooks Errors</Tab>
                        <Tab tabId="APP_SETTINGS">App Settings</Tab>
                        <Tab tabId="ACCOUNT">Account</Tab>
                        <EmptyTab style={{ flex: 2 }} />
                    </Row>

                    <GeneralSettings location={location} onSubmit={onSubmit} />
                    <PayrollSettings location={location} onSubmit={onSubmit} />
                    <AppSettings location={location} onSubmit={onSubmit} />
                    <QBSync location={location} onSubmit={onSubmit} />
                    <QBSyncErrors location={location} onSubmit={onSubmit} />
                    <AccountSettings location={location} onSubmit={onSubmit} />
                </TabContainer>
            </Content>
        </Layout>
    );
};
export default ScreenSettings;
